import { Box, Checkbox, styled, Typography } from '@mui/material';
import { AuthError } from 'firebase/auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Footer } from '../components/Footer';
import { GeneralNavbar } from '../components/GeneralNavbar';
import { register } from '../services/firebaseAuth';
import { getAuthErrorMessage, validatePassword, validatePasswordForKeywords } from '../utils';
import { TextInputField } from '../components/TextInputField';
import { PasswordField } from '../components/PasswordField';
import { addProducer } from '../services/cloudFirestore';
import { useNavigate } from 'react-router-dom';
import { StyledGreenButton, StyledLink, StyledRowBox } from '../theme/StyledElements';

const StyledBox = styled(Box)(() => ({
    width: '650px',
    marginLeft: '20%'
}));

const StyledLoginHeading = styled(Typography)(() => ({
    color: '#134C03',
    margin: '40px 0px 10px 0px',
    fontWeight: 'normal'
}));

const StyledLoginSubHeading = styled(Typography)(() => ({
    color: '#134C03',
    fontWeight: 'normal',
    width: '80%'
}));

export function Register(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setloading] = useState(false);
    const [firstname, setFirstname] = useState<string>('');
    const [lastname, setLastname] = useState<string>('');
    const [emailId, setEmailId] = useState<string>('');

    const validationSchema = yup.object({
        email: yup.string().email(t('enterValidEmailMessage')),
        password: yup
            .string()
            .test('', t('passwordKeywordMessage'), (value): boolean => {
                return validatePasswordForKeywords(value, firstname, lastname, emailId);
            })
            .test('', t('passwordErrorMessage'), (value): boolean => {
                if (value) {
                    return validatePassword(value);
                }
                return true;
            })
            .min(12, t('passwordMinLengthMessage'))
            .max(32, t('passwordMaxLengthMessage')),
        confirmPassword: yup
            .string()
            .oneOf([yup.ref('password'), null], t('passwordMismatchMessage'))
    });

    const registerForm = useFormik({
        initialValues: {
            lastName: '',
            firstName: '',
            email: '',
            confirmPassword: '',
            password: '',
            acceptConditions: false
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!registerForm.isValid) return;
            setloading(true);
            const { lastName, firstName, email, confirmPassword, password, acceptConditions } =
                values;
            try {
                const { user } = await register(email, password);
                if (user && user.uid) {
                    await addProducer({
                        idProducteur: user.uid,
                        mail: email,
                        prenom: firstName,
                        nom: lastName
                    });
                }
                navigate('/subscription');
            } catch (authError) {
                const error = authError as AuthError;
                registerForm.setErrors(getAuthErrorMessage(error.code, t));
            }
            setloading(false);
        }
    });

    const { values, errors, handleChange, submitForm, isValid } = registerForm;

    return (
        <>
            <GeneralNavbar isBottomBorderRequired />
            <StyledBox>
                <StyledLoginHeading variant="h2">{t('creationOfAccount')}</StyledLoginHeading>
                <TextInputField
                    id="lastName"
                    name="lastName"
                    label={t('lastName')}
                    value={values.lastName}
                    onChange={(e): void => {
                        handleChange(e);
                        setFirstname(e.currentTarget.value);
                    }}
                    error={Boolean(errors.lastName)}
                    helperText={errors.lastName}
                    sx={{
                        width: '50%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <br />
                <TextInputField
                    id="firstName"
                    name="firstName"
                    label={t('firstName')}
                    value={values.firstName}
                    onChange={(e): void => {
                        handleChange(e);
                        setLastname(e.currentTarget.value);
                    }}
                    error={Boolean(errors.firstName)}
                    helperText={errors.firstName}
                    sx={{
                        width: '50%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <br />
                <TextInputField
                    id="email"
                    name="email"
                    label={t('email')}
                    value={values.email}
                    onChange={(e): void => {
                        handleChange(e);
                        setEmailId(e.currentTarget.value);
                    }}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    sx={{
                        width: '50%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <br />
                <PasswordField
                    id="password"
                    name="password"
                    label={t('yourPassword')}
                    value={values.password}
                    onChange={handleChange}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                    sx={{
                        width: '50%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginTop: '10px',
                        marginBottom: '15px'
                    }}>
                    {t('passwordCriteriaHeading')}
                </StyledLoginSubHeading>
                <StyledLoginSubHeading variant="h5">{t('passwordCriteria1')}</StyledLoginSubHeading>
                <StyledLoginSubHeading variant="h5">{t('passwordCriteria2')}</StyledLoginSubHeading>
                <StyledLoginSubHeading variant="h5">{t('passwordCriteria3')}</StyledLoginSubHeading>
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginLeft: '5px'
                    }}>
                    {t('passwordCriteria4')}
                </StyledLoginSubHeading>
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginLeft: '5px'
                    }}>
                    {t('passwordCriteria5')}
                </StyledLoginSubHeading>
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginLeft: '5px'
                    }}>
                    {t('passwordCriteria6')}
                </StyledLoginSubHeading>
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginLeft: '5px'
                    }}>
                    {t('passwordCriteria7')}
                </StyledLoginSubHeading>
                <StyledLoginSubHeading
                    variant="h5"
                    sx={{
                        marginBottom: '15px',
                        marginLeft: '5px'
                    }}>
                    {t('passwordCriteria8')}
                </StyledLoginSubHeading>
                <PasswordField
                    id="confirmPassword"
                    name="confirmPassword"
                    label={t('passwordConfirmation')}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword}
                    sx={{
                        width: '50%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <StyledLoginHeading variant="h2">{t('termsAndService')}</StyledLoginHeading>
                <StyledLink
                    href="https://www.amo.farm/conditions-generales-dutilisation-et-de-vente-damo/"
                    target="_blank"
                    variant="h5"
                    sx={{ margin: '20px 0px' }}>
                    {t('consultConditionsOfUse')}
                </StyledLink>
                <StyledRowBox>
                    <Checkbox
                        id="acceptConditions"
                        name="acceptConditions"
                        checked={values.acceptConditions}
                        onChange={handleChange}
                        sx={{
                            color: '#134C03',
                            '&.Mui-checked': {
                                color: '#134C03'
                            }
                        }}
                    />
                    <StyledLoginSubHeading
                        variant="h5"
                        sx={{
                            marginTop: '15px'
                        }}>
                        {t('acceptTermsAndService')}
                    </StyledLoginSubHeading>
                </StyledRowBox>
                <StyledGreenButton
                    loading={loading}
                    disabled={
                        !isValid ||
                        !Boolean(values.email) ||
                        !Boolean(values.firstName) ||
                        !Boolean(values.lastName) ||
                        !Boolean(values.password) ||
                        !Boolean(values.confirmPassword) ||
                        !Boolean(values.acceptConditions)
                    }
                    type="button"
                    onClick={async (e): Promise<void> => {
                        e.preventDefault();
                        await submitForm();
                    }}
                    sx={{ margin: '40px 0px 80px 0px' }}>
                    <Typography variant="caption" color="#FFFFFF">
                        {t('createAccount')}
                    </Typography>
                </StyledGreenButton>
            </StyledBox>
            <Footer />
        </>
    );
}
